/**
 * Configurações — ambiente parametrizável das integrações (Entrega 0, R3–R5).
 *
 * Tela READ-ONLY sobre o registry de `packages/config` (D-R3R5-04). Para cada
 * integração mostra:
 *   - o ESTADO: `simulador` (falta credencial) ou `real` (tudo configurado);
 *   - a lista de segredos como `configurado`/`faltando` — NUNCA o valor
 *     (D-R3R5-02). O servidor devolve só a presença (booleano); a tela não tem
 *     acesso a nenhum valor de segredo;
 *   - o flag: indicador on/off e o NOME da variável de ambiente a setar.
 *
 * O toggle é INFORMATIVO nesta entrega: ativar de verdade é preencher o
 * segredo + setar o flag no ambiente e redeployar (o brief). A tela diz qual
 * variável setar, não muta o ambiente a partir do browser.
 *
 * Segue o padrão de `pages/Sincronizacao.ts`: uma View pura (testável com um
 * dataset injetado) e uma página que carrega via React Query/`PlatformApiClient`.
 *
 * Migrado para `.tsx`/JSX real (Cluster E — Financeiro & Sistema, redesign
 * por perfil; ver `docs/architecture/ADR-0011-*.md`): troca mecânica de
 * sintaxe (`createElement` → JSX). Esta tela já estava tokenizada e já usava
 * ícones `lucide-react` nos selos de estado/flag/segredo antes desta frente —
 * nenhum ícone novo foi necessário, nenhum `data-*`/aria/texto/rota ou
 * contrato real de integração/flag muda (preservado 1:1).
 */
// Import explícito de `React` — ver o mesmo comentário em `KpiCard.tsx`.
import React, { type ReactElement, type ReactNode } from "react";
import { CheckCircle2, Power, type LucideIcon, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "../ui/index.js";
import { SalesOriginsPanel } from "../components/SalesOriginsPanel.js";
import { RecordTable } from "../components/RecordTable.js";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import { useIntegrations, useMyAccess } from "../queries.js";
import type { PlatformIntegrationStatus } from "../api/types.js";

/** Ícone 14px, herda o tom do selo via `currentColor`; decorativo. */
function cfgIcon(Icon: LucideIcon): ReactElement {
  return <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden focusable={false} />;
}

const STATE_LABELS: Record<PlatformIntegrationStatus["state"], string> = {
  real: "real",
  simulator: "simulador"
};

const STATE_DETAIL: Record<PlatformIntegrationStatus["state"], string> = {
  real: "Todas as credenciais configuradas: o adapter real é usado.",
  simulator: "Falta credencial: o adapter simulado (mesma interface) responde; o caminho real retorna 503 nomeando o segredo."
};

export interface ConfiguracoesViewProps {
  integrations: PlatformIntegrationStatus[];
  /** Atalho para Usuários e acessos; só administrador (default: oculto; a página liga para administrador). */
  canManageUsers?: boolean;
  /** Painel de canais de origem das vendas (F4); a página injeta o painel ligado à API. */
  originsPanel?: ReactNode;
}

/**
 * Selo de estado da integração (ícone + texto, nunca só cor — régua §2). `real`
 * = tudo configurado (success/check); `simulator` = falta credencial
 * (warning/alerta). `data-integration-state` e o rótulo seguem no wrapper.
 */
function stateBadge(state: PlatformIntegrationStatus["state"]): ReactElement {
  const real = state === "real";
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold " +
        (real ? "border-success text-success" : "border-warning text-warning")
      }
      data-integration-state={state}
    >
      {cfgIcon(real ? CheckCircle2 : TriangleAlert)}
      <span>{STATE_LABELS[state]}</span>
    </span>
  );
}

/** Bloco de uma integração: estado, segredos (configurado/faltando) e flag. */
function integrationBlock(integration: PlatformIntegrationStatus): ReactElement {
  const flagOn = integration.flag.enabled;
  return (
    <section
      key={integration.id}
      className="page__section flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1"
      data-section="integracao"
      data-integration={integration.id}
      data-state={integration.state}
    >
      <h3 className="flex flex-wrap items-center gap-2 text-lg font-extrabold text-ink">
        <span>{integration.name}</span>
        <span className="integration__state inline-flex">{stateBadge(integration.state)}</span>
      </h3>
      <p className="page__note text-sm text-muted">{STATE_DETAIL[integration.state]}</p>
      <p className="page__note flex flex-wrap items-center gap-2 text-sm text-muted" data-integration-flag={integration.flag.name}>
        <span>Flag:</span>
        <span
          className={
            "inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold " +
            (flagOn ? "border-success text-success" : "border-border-strong text-body")
          }
          data-flag-enabled={flagOn ? "on" : "off"}
        >
          {cfgIcon(Power)}
          <span>{flagOn ? "ligado (on)" : "desligado (off)"}</span>
        </span>
        <span>— defina</span>
        <code className="rounded-control bg-surface-soft px-1.5 py-0.5 text-xs text-ink">{`${integration.flag.name}=on`}</code>
        <span>no ambiente para ativar (informativo: requer redeploy).</span>
      </p>
      <RecordTable<{ name: string; configured: boolean }>
        bare
        caption={`Segredos exigidos por ${integration.name} (nome e situação, nunca o valor)`}
        rows={integration.secrets}
        idOf={(row) => row.name}
        emptyMessage="Nenhum segredo exigido por esta integração."
        // Fase 7 (Plano de Ataque R8): rollout mecânico do primitivo da Fase 6.
        animateRows
        columns={[
          {
            key: "name",
            label: "Variável de ambiente",
            render: (row) => <code className="text-sm text-ink">{row.name}</code>
          },
          {
            key: "configured",
            label: "Situação",
            // Situação por ÍCONE + TEXTO (nunca só cor). NUNCA exibe valor de
            // segredo — só a presença (booleano) que o servidor devolve.
            render: (row) => (
              <span
                className={
                  "inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold " +
                  (row.configured ? "border-success text-success" : "border-warning text-warning")
                }
                data-secret-configured={row.configured ? "yes" : "no"}
              >
                {cfgIcon(row.configured ? CheckCircle2 : TriangleAlert)}
                <span>{row.configured ? "configurado" : "faltando"}</span>
              </span>
            )
          }
        ]}
      />
    </section>
  );
}

/** Atalho para a gestão de usuários; só aparece para administrador (ou quando o papel ainda não é conhecido). */
function UsersShortcut(): ReactElement {
  return (
    <section className="flex flex-wrap items-center justify-between gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1" data-section="usuarios-atalho">
      <div>
        <h3 className="text-lg font-extrabold text-ink">Usuários e acessos</h3>
        <p className="m-0 text-sm text-muted">Cadastre a equipe, defina o que cada pessoa pode ver e editar e bloqueie um acesso na hora.</p>
      </div>
      <Link to="/configuracoes/usuarios" className="inline-flex min-h-[44px] items-center rounded-control bg-brand px-4 font-bold text-on-brand hover:bg-brand-pressed">
        Gerenciar usuários
      </Link>
    </section>
  );
}

export function ConfiguracoesView({ integrations, canManageUsers = false, originsPanel }: ConfiguracoesViewProps): ReactElement {
  return (
    <div className="page page--configuracoes flex flex-col gap-6" data-page="configuracoes">
      <PageHeader
        title="Configurações de integração"
        description="Estado de cada integração e o que falta para ela sair do simulador. Somente leitura: o valor de um segredo nunca é exibido, apenas se a variável está configurada ou faltando."
      />
      {canManageUsers ? <UsersShortcut /> : null}
      {originsPanel ?? null}
      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-2">
      {integrations.length === 0 ? <StatusPanel kind="empty" title="Nenhuma integração registrada" /> : integrations.map(integrationBlock)}
      </div>
    </div>
  );
}

export function Configuracoes(): ReactElement {
  const query = useIntegrations();
  const me = useMyAccess();
  if (query.isPending) {
    return <StatusPanel kind="loading" title="Carregando configuração das integrações…" />;
  }
  if (query.error) {
    return <StatusPanel kind="error" title="Não foi possível carregar a configuração das integrações" detail={errorDetail(query.error)} />;
  }
  // `null` = a rota de configuração não está publicada neste ambiente. É
  // "aguardando contrato", não "nada configurado": a tela declara a ausência.
  if (query.data === null || query.data === undefined) {
    return (
      <StatusPanel
        kind="awaiting_source"
        title="Configuração de integrações não publicada neste ambiente"
        detail="A rota /api/platform/config/integrations não respondeu aqui. Em dev/E2E ela é servida pelo apps/api-dev; a paridade no Worker de produção é pendência registrada."
      />
    );
  }
  return <ConfiguracoesView integrations={query.data} canManageUsers={!me.data || me.data.role === "admin"} originsPanel={<SalesOriginsPanel />} />;
}
